import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { AdminTone } from './status';

export interface IconTileProps {
  tone?: Exclude<AdminTone, 'grey'> | 'grey';
  className?: string;
  children: ReactNode;
}

/** Small tinted square that holds an icon (settings rows, section headers). */
export function IconTile({ tone = 'grey', className, children }: IconTileProps) {
  return (
    <span className={cn('adm-icon-tile', tone !== 'grey' && `adm-icon-tile--${tone}`, className)} aria-hidden="true">
      {children}
    </span>
  );
}

export interface SettingRowProps {
  /** Optional icon (wrapped in an IconTile). */
  icon?: ReactNode;
  iconTone?: IconTileProps['tone'];
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned control (Switch, Button, Select…). */
  control?: ReactNode;
  /** Extra content under the description (inputs, notes). */
  children?: ReactNode;
  /** id of the control, so the title can be its <label>. */
  htmlFor?: string;
  className?: string;
}

/** One setting: icon tile, title + description on the left, control on the right. */
export function SettingRow({ icon, iconTone, title, description, control, children, htmlFor, className }: SettingRowProps) {
  const TitleTag = htmlFor ? 'label' : 'p';
  return (
    <div className={cn('flex items-start gap-3 py-3.5', className)}>
      {icon && <IconTile tone={iconTone}>{icon}</IconTile>}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5">
          <div className="min-w-0 flex-1 basis-[200px]">
            <TitleTag {...(htmlFor ? { htmlFor } : {})} className="block text-[13.5px] font-semibold leading-snug text-foreground">
              {title}
            </TitleTag>
            {description && <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted-foreground">{description}</p>}
          </div>
          {control && <div className="flex shrink-0 items-center gap-2 pt-0.5">{control}</div>}
        </div>
        {children && <div className="mt-3">{children}</div>}
      </div>
    </div>
  );
}
